import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../AxiosInstance";
import { Outlet } from "react-router-dom";
import AudioPermissionRequest from "../components/AudioPermissionRequest";
import { toast } from "react-toastify";
import { queryClient } from "../App";

export const SalesNewOrderContext = createContext();

// Variable globale pour stocker le contexte audio
let globalAudioContext = null;

// Fonction pour jouer le son de notification avec gestion des limitations navigateur
export const playNotificationSound = () => {
  try {
    // Solution 1: Essayer de jouer le fichier audio
    // const audio = new Audio("/assets/audios/notif.wav");
    const audio = new Audio("/assets/audios/bringme.wav");
    audio.volume = 1;

    // Précharger l'audio pour éviter les problèmes de latence
    audio.preload = "auto";

    // Essayer de jouer avec gestion d'erreur
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log("Son de notification joué avec succès");
        })
        .catch((error) => {
          console.log("Impossible de jouer le son:", error);

          // Solution 2: Utiliser l'API Web Audio comme fallback
          playWebAudioNotification();
        });
    }
  } catch (error) {
    console.log("Erreur lors de la lecture du son:", error);
    // Solution 2: Utiliser l'API Web Audio comme fallback
    playWebAudioNotification();
  }
};

// Fonction alternative utilisant l'API Web Audio
export const playWebAudioNotification = () => {
  try {
    // Créer un nouveau contexte audio à chaque fois pour éviter les problèmes de réutilisation
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Créer un oscillateur pour générer un bip
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    // Connecter les nœuds
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configurer le son (fréquence, volume, durée)
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Volume 30%

    // Jouer le son
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2); // Durée 200ms

    // Nettoyer le contexte après utilisation
    setTimeout(() => {
      audioContext.close();
    }, 300);

    console.log("Son de notification généré avec Web Audio API");
  } catch (error) {
    console.log("Erreur avec Web Audio API:", error);

    // Solution 3: Notification visuelle en fallback
    showVisualNotification();
  }
};

// Fonction pour notification visuelle (fallback ultime)
export const showVisualNotification = () => {
  try {
    // Supprimer les anciennes notifications pour éviter l'accumulation
    const existingNotifications = document.querySelectorAll(
      ".new-order-notification"
    );
    existingNotifications.forEach((notification) => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    });

    // Créer une notification visuelle temporaire
    const notification = document.createElement("div");
    notification.className = "new-order-notification";
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff4757;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      z-index: 99999;
      font-weight: bold;
      font-size: 14px;
      animation: slideInNotification 0.5s ease-out;
      box-shadow: 0 4px 20px rgba(255, 71, 87, 0.3);
      border: 2px solid #ff6b7a;
      min-width: 200px;
      text-align: center;
    `;

    // Ajouter l'animation CSS si elle n'existe pas déjà
    if (!document.querySelector("#notification-styles")) {
      const style = document.createElement("style");
      style.id = "notification-styles";
      style.textContent = `
        @keyframes slideInNotification {
          0% {
            transform: translateX(100%) translateY(-20px);
            opacity: 0;
          }
          100% {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
        }
        @keyframes pulseNotification {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .new-order-notification {
          animation: slideInNotification 0.5s ease-out, pulseNotification 2s ease-in-out infinite;
        }
      `;
      document.head.appendChild(style);
    }

    notification.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
        <span style="font-size: 20px;">🔔</span>
        <div>
          <div style="font-weight: bold; margin-bottom: 2px;">Nouvelle commande !</div>
          <div style="font-size: 12px; opacity: 0.9;">Vérifiez vos commandes</div>
        </div>
      </div>
    `;

    // Ajouter au body
    document.body.appendChild(notification);

    // Supprimer après 4 secondes
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation =
          "slideInNotification 0.3s ease-in reverse";
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }
    }, 4000);

    console.log("Notification visuelle affichée");
  } catch (error) {
    console.log("Erreur avec notification visuelle:", error);
  }
};

// Hook personnalisé pour les nouvelles commandes
const useNewOrders = () => {
  const previousDataRef = useRef(null);
  const [lastNotificationTime, setLastNotificationTime] = useState(0);

  const { data, isLoading, error, refetch, isFetched, isFetching } = useQuery({
    queryKey: ["new-orders", "pending"],
    queryFn: async () => {
      // const response = await axiosInstance.get(`c/sales/?limit=5&offset=0`);
      const response = await axiosInstance.get(
        `c/sales/?limit=5&offset=0&status=pending`
      );
      return response.data;
    },
    refetchInterval: 20000, // Rafraîchir toutes les 30 secondes
    refetchIntervalInBackground: true, // Continuer même si l'onglet n'est pas actif
    staleTime: 20000, // Les données sont considérées comme fraîches pendant 30 secondes
    gcTime: 5 * 60 * 1000, // Garder en cache pendant 5 minutes
    retry: 3, // Réessayer 3 fois en cas d'échec
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Retry avec backoff exponentiel
  });

  // Vérifier s'il y a de nouvelles données et jouer le son
  useEffect(() => {
    // console.log({data});
    if (data && !isFetching) {
      const currentResults = data.results || [];
      // const previousResults = previousDataRef.current.results || [];
      console.log({ currentResults });
      // Vérifier s'il y a de nouvelles commandes
      if (currentResults.length > 0) {
        const now = Date.now();
        // Éviter les notifications trop fréquentes (minimum 2 secondes entre chaque)
        // console.log(`Nouvelles commandes détectées! Ancien: ${previousResults.length}, Nouveau: ${currentResults.length}`);
        playNotificationSound();
        queryClient.invalidateQueries({ queryKey: ["sale-list"] });
        // mettre un toast
        toast.success("Nouvelle commande détectée", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        setLastNotificationTime(now);
      }

      // Mettre à jour la référence avec les données actuelles
      previousDataRef.current = data;
    }
  }, [data, isFetching]);

  return {
    orders: data?.results || [],
    totalCount: data?.count || 0,
    isLoading,
    error,
    refetch,
  };
};

export const SalesNewOrderProvider = ({ children }) => {
  const newOrdersData = useNewOrders();
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);

  const handleAudioPermissionGranted = (audioContext) => {
    globalAudioContext = audioContext;
    setAudioPermissionGranted(true);
  };

  return (
    <SalesNewOrderContext.Provider value={newOrdersData}>
      {children || <Outlet />}
      <AudioPermissionRequest
        onPermissionGranted={handleAudioPermissionGranted}
      />
    </SalesNewOrderContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useSalesNewOrder = () => {
  const context = useContext(SalesNewOrderContext);
  if (!context) {
    throw new Error(
      "useSalesNewOrder doit être utilisé dans un SalesNewOrderProvider"
    );
  }
  return context;
};
